import SecurePrescriptionPageService from "@/core/services/SecurePrescriptionPageService";
import TreatmentSheets from "@/medication-checkup/services/TreatmentSheets";

/**
 * A page of a scanned prescription, ready to show.
 */
export interface PrescriptionPage {
    url: string;
    /**
     * How many pages the prescription has — told with the first page only,
     * 1 on the others.
     */
    pageCount: number;
}

/**
 * The pages fetched so far, by prescription key and page, shared by every
 * view of the collection: the strip, the full screen viewer and the dosage
 * dialog show the same image from one request. A request in flight is
 * shared too, and a failed one forgotten so it can be retried.
 */
const pages = new Map<string, Promise<PrescriptionPage>>();

/**
 * Every image url created, released together.
 */
const created: string[] = [];

/**
 * Bumped by every release, so a request still in flight then knows its page
 * is no longer wanted.
 */
let generation = 0;

const PrescriptionPages = () => {
    return {
        /**
         * A page of a prescription, fetched the first time only.
         */
        fetch(key: string, page: number): Promise<PrescriptionPage> {
            const id = `${key}#${page}`;
            const known = pages.get(id);

            if (known) {
                return known;
            }

            const born = generation;

            const request = SecurePrescriptionPageService.fetch(TreatmentSheets.urls(key).thumbnail.large, page)
                .then(({blob, pageCount}) => {
                    const url = window.URL.createObjectURL(blob);

                    // Released while in flight: nothing shows the page any more.
                    if (born !== generation) {
                        window.URL.revokeObjectURL(url);
                    } else {
                        created.push(url);
                    }

                    return {url, pageCount};
                });

            request.catch(() => {
                // A page asked again after a release has a request of its own.
                if (pages.get(id) === request) {
                    pages.delete(id);
                }
            });
            pages.set(id, request);

            return request;
        },

        /**
         * Let go of every page fetched, once nothing shows them any more.
         */
        release(): void {
            generation += 1;
            pages.clear();
            created.splice(0).forEach(url => window.URL.revokeObjectURL(url));
        }
    };
};

export default PrescriptionPages();
